/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import {
  StatusBanner,
  UploadArea,
  FilesList,
  Flex,
  Form,
  FormLayout,
  List,
  Spinner,
  Text,
  TextHeading,
} from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { UPLOAD_HELPER_MESSAGES, useErrorModal, useSpaNavigate } from "shared-ui";
import type { UploadsState } from "shared-utils";
import { uploadManagerHookFactory } from "shared-utils";
import { styled } from "styled-components";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { PAGE_TYPE, composePath } from "../../hooks/navigation-next/use-funding-application-navigation-next";
import { useBankStatementSubmitMutation } from "../../queries/bank-connect/bank-statement.mutations";
import { useBankingSources } from "../../queries/bank-connect/queries";
import { COMMON_MESSAGES } from "../common-messages";
import {
  BANK_CONNECT_API,
  BANK_CONNECT_SOURCES_API,
  BANK_STATEMENTS_CONFIRM_UPLOAD_API,
  BANK_STATEMENTS_CREATE_UPLOAD_API,
  BANK_STATEMENTS_DELETE_UPLOAD_API,
  UPLOAD_STATEMENTS_BANK_GROUP_LOCATION_STATE,
  UPLOAD_STATEMENTS_SIZE_LIMIT_MB,
  UPLOAD_STATEMENTS_SUPPORTED_EXTENSIONS,
} from "../constants";

import { MESSAGES } from "./message-definitions";

const ScrollableContainerStyle = styled(Flex)`
  max-height: 160px;
  overflow-y: scroll;
`;

type BankStatementUploadFile = { name: string; objectKey: string };

type BankStatementUploadsFormFields = {
  bank_name: string;
  file_uploads: BankStatementUploadFile[];
};

type UploadBankStatementsProps = {
  bankingPath?: string;
};

export const UploadBankStatements = ({ bankingPath = composePath(PAGE_TYPE.BANKING) }: UploadBankStatementsProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: bankSourcesData } = useBankingSources(customerDomain);
  const { navigate } = useSpaNavigate("FundingApp");
  const { state } = useLocation();
  const bankGroupUuid: string | null = (() => {
    if (state) {
      return state[UPLOAD_STATEMENTS_BANK_GROUP_LOCATION_STATE];
    }
    return null;
  })();
  const { openErrorModal } = useErrorModal();
  const [formError, setFormError] = useState<string | undefined>();
  const submitBankStatements = useBankStatementSubmitMutation(customerDomain);
  const queryClient = useQueryClient();
  /**
   * Handles upload attach file
   */
  const useUploadManager = uploadManagerHookFactory<BankStatementUploadFile>({
    createUrlPath: BANK_STATEMENTS_CREATE_UPLOAD_API.replace(":customerSlug", customerDomain),
    confirmUploadPath: BANK_STATEMENTS_CONFIRM_UPLOAD_API.replace(":customerSlug", customerDomain),
    deleteUploadPath: BANK_STATEMENTS_DELETE_UPLOAD_API.replace(":customerSlug", customerDomain),
    files: [],
    supportedFileExtensions: UPLOAD_STATEMENTS_SUPPORTED_EXTENSIONS,
    sizeLimitMB: UPLOAD_STATEMENTS_SIZE_LIMIT_MB,
  });

  const bankGroup =
    (bankGroupUuid &&
      bankSourcesData?.bankingStatementsGroups?.filter((group) => group && group.groupUuid === bankGroupUuid)) ||
    null;

  const initialBankName = bankGroup && bankGroup[0].name;

  const previouslyUploadedStatements = bankGroup && bankGroup[0].previouslyUploadedStatements;

  const onBack = () => {
    navigate(bankingPath);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = Yup.object({
    bank_name: Yup.string()
      .trim()
      .required(intl.formatMessage(MESSAGES.bankNameRequiredError))
      .default(initialBankName || ""),
    file_uploads: Yup.array().min(1, intl.formatMessage(MESSAGES.uploadFileRequiredError)).of(Yup.object()).default([]),
  });

  const form = useForm<BankStatementUploadsFormFields>({
    resolver: yupResolver(schema),
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const { bank_name } = form.watch();

  const fileUploadProps = useUploadManager();

  // Generate helperText from upload constraints
  const helperText = useMemo(() => {
    let text = "";

    if (fileUploadProps.sizeLimitMB) {
      text += intl.formatMessage(UPLOAD_HELPER_MESSAGES.uploadFileSizeLimit, {
        sizeLimitMb: fileUploadProps.sizeLimitMB,
      });
    }

    if (fileUploadProps.supportedFileExtensions && fileUploadProps.supportedFileExtensions.length > 0) {
      text +=
        " " +
        intl.formatMessage(UPLOAD_HELPER_MESSAGES.supportedFileExtensionUpload, {
          supportedFileExtensions: fileUploadProps.supportedFileExtensions.join(", "),
        });
    }

    return text.trim();
  }, [fileUploadProps.sizeLimitMB, fileUploadProps.supportedFileExtensions, intl]);

  useEffect(() => {
    const uploads =
      fileUploadProps.uploadsState?.uploads
        ?.map((uploadState) => ({
          name: uploadState.uploadedFile?.name ?? "",
          objectKey: uploadState.uploadedFile?.objectKey ?? "",
        }))
        .filter((uploadedFile) => uploadedFile.name && uploadedFile.objectKey) || [];
    form.setValue("file_uploads", uploads);
  }, [fileUploadProps.uploadsState, form]);

  const onSubmitStatements = useCallback(() => {
    // Refresh the bank sources and initial bank data for navigation on submit
    queryClient.invalidateQueries({
      queryKey: [BANK_CONNECT_SOURCES_API.replace(":customerSlug", customerDomain), customerDomain],
    });
    queryClient.invalidateQueries({
      queryKey: [BANK_CONNECT_API.replace(":customerSlug", customerDomain), customerDomain],
    });
  }, [customerDomain, queryClient]);

  const submitClickHandler = (formData: BankStatementUploadsFormFields) => {
    const objectKeys = formData.file_uploads.map((file) => file.objectKey);
    if (objectKeys.length) {
      setIsSubmitting(true);
      submitBankStatements
        .mutateAsync({
          object_keys: objectKeys,
          group: formData.bank_name,
        })
        .then(() => {
          onSubmitStatements();
          navigate(bankingPath);
        })
        .catch((error) => {
          openErrorModal({ variant: "submit-error", error });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      openErrorModal({ variant: "submit-error", error: new Error("Submit path is not defined") });
    }
  };

  const onUploadsComplete = (formData: BankStatementUploadsFormFields) => {
    try {
      submitClickHandler(formData);
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const existingFilesUploadsState: UploadsState<string> = {
    uploadsStarted: true,
    uploadsComplete: true,
    uploads:
      previouslyUploadedStatements?.map((statement) => ({
        status: "complete",
        file: new File([], statement!.fileName),
        uploadedFile: statement!.fileType,
      })) ?? [],
  };

  const prefilledBankNameUploadHeadingText = intl.formatMessage(MESSAGES.UploadYourBankHeading, {
    bankName: initialBankName ? initialBankName : bank_name,
  });

  const description = (
    <Flex gap="4" direction="column">
      <Text>{intl.formatMessage(MESSAGES.statementsUploadDescription)}</Text>
      <Text>{intl.formatMessage(MESSAGES.uploadRequirements)}</Text>
      <List type="bullet">
        <List.Item>
          <Text>{intl.formatMessage(MESSAGES.uploadRequirementsPoint1)}</Text>
        </List.Item>
        <List.Item>
          <Text>{intl.formatMessage(MESSAGES.uploadRequirementsPoint2)}</Text>
        </List.Item>
      </List>
    </Flex>
  );

  return bankSourcesData ? (
    <Flex gap="4" direction="column">
      <Form
        primaryAction={{
          content: intl.formatMessage(COMMON_MESSAGES.submitLabel),
          analyticsId: "bank-statement-upload-submit",
          disabled:
            !fileUploadProps.uploadsState?.uploads.length ||
            fileUploadProps.uploadsState?.uploadsComplete === false ||
            !!formError ||
            isSubmitting,
        }}
        secondaryAction={{
          onAction: onBack,
          content: intl.formatMessage(COMMON_MESSAGES.backLabel),
          analyticsId: "bank-statement-upload-back",
        }}
        onSubmit={form.handleSubmit(onUploadsComplete, (e) => addError(e, { message: "form error" }))}
      >
        <FormLayout>
          {description}
          {formError && <StatusBanner variant="critical" subheading={formError} />}
          {initialBankName ? null : (
            <RhfTextField<BankStatementUploadsFormFields>
              form={form}
              name="bank_name"
              defaultValue={bank_name || ""}
              placeholder="Citi Bank"
              label={intl.formatMessage(MESSAGES.uploadBankNameFieldLabel)}
            />
          )}
          <UploadArea
            label={
              initialBankName
                ? prefilledBankNameUploadHeadingText
                : intl.formatMessage(MESSAGES.statementsUploadHeading)
            }
            testId="bank-statements-uploader"
            onFilesAdded={fileUploadProps.onFilesAdded}
            acceptedFileTypes={{ "text/csv": [".csv"], "application/pdf": [".pdf"] }}
            sizeLimitMB={fileUploadProps.sizeLimitMB}
            helperText={helperText}
            multiple
          />
          {fileUploadProps.uploadsState && fileUploadProps.uploadsState.uploads.length > 0 ? (
            <FilesList uploadsState={fileUploadProps.uploadsState} onFileRemoved={fileUploadProps.onFileRemoved} />
          ) : null}
        </FormLayout>
      </Form>
      {bankGroup && previouslyUploadedStatements && previouslyUploadedStatements.length > 0 && (
        <Flex direction="column" gap="2">
          <TextHeading>{intl.formatMessage(MESSAGES.previouslyUploadedHeading)}</TextHeading>
          <ScrollableContainerStyle>
            <FilesList uploadsState={existingFilesUploadsState} />
          </ScrollableContainerStyle>
        </Flex>
      )}
    </Flex>
  ) : (
    <Flex direction="column" gap="8">
      <Text>{intl.formatMessage(MESSAGES.statementUploadDataLoading)}</Text>
      <Spinner size={32} />
    </Flex>
  );
};
